import { useEffect } from 'react';
import { useBeforeUnload, useNavigate } from 'react-router-dom';
import { useUnsavedChangesContext } from '../hooks/useUnsavedChangesWarning';

/**
 * Guard pour empêcher la navigation en cas de modifications non sauvegardées
 * Fonctionne comme les Guards d'Angular
 * 
 * @param {boolean} hasUnsavedChanges - Indique s'il y a des modifications non sauvegardées
 * @param {string} message - Message de confirmation
 */
export function UnsavedChangesGuard({ hasUnsavedChanges = false, message = 'Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter cette page ?' }) {
  const context = useUnsavedChangesContext();
  const { hasUnsavedChangesRef, currentMessageRef, allowNextNavigationRef } = context;
  const navigate = useNavigate();

  // Mettre à jour le contexte
  useEffect(() => {
    console.log('🛡️ UnsavedChangesGuard: hasUnsavedChanges changé:', hasUnsavedChanges);
    hasUnsavedChangesRef.current = hasUnsavedChanges;
    if (message) {
      currentMessageRef.current = message;
    }
  }, [hasUnsavedChanges, message, hasUnsavedChangesRef, currentMessageRef]);

  // Bloquer le refresh et la fermeture du navigateur
  useBeforeUnload((event) => {
    if (hasUnsavedChanges && !allowNextNavigationRef.current) {
      event.preventDefault();
      return (event.returnValue = message);
    }
  });

  // Intercepter manuellement les clics sur les liens
  useEffect(() => {
    const handleClick = (event) => {
      const target = event.target.closest('a');
      if (target && target.href) {
        // Vérifier si c'est un lien interne
        const url = new URL(target.href);
        if (window.location.origin === url.origin) {
          // C'est un lien interne - vérifier si on doit bloquer
          if (hasUnsavedChangesRef.current && !allowNextNavigationRef.current) {
            event.preventDefault();
            if (window.confirm(currentMessageRef.current)) {
              allowNextNavigationRef.current = true;
              navigate(url.pathname + url.search + url.hash);
            }
          }
        }
      }
    };

    document.addEventListener('click', handleClick, true);
    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [navigate, hasUnsavedChangesRef, currentMessageRef]);

  return null;
}

export default UnsavedChangesGuard;
